// src/views/admin_sys/appointment_list/admin_appointment_list.jsx
import { useState, useEffect, useCallback } from "react"
import { usersAPI, appointmentsAPI } from "services/api"
import { useAuth } from "context/AuthContext"

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const getInitials = (name) => {
    if (!name) return "?"
    const parts = String(name).trim().split(/\s+/)
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase()
}

const getLocalDateString = (iso) => {
    if (!iso) return ""
    return iso.split("T")[0]
}

const getDayName = (dateString) => {
    const days = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"]
    const date = new Date(dateString.replace(/-/g, "/"))
    return days[date.getDay()]
}

const formatDateWithDay = (iso) => {
    const local = getLocalDateString(iso)
    if (!local) return "Geçersiz Tarih"
    const [y, m, d] = local.split("-")
    return `${d}.${m}.${y} (${getDayName(local)})`
}

const calculateEndTime = (startTime, durationMinutes) => {
    if (!startTime || !durationMinutes) return "-"
    const [h, min] = startTime.split(":").map(Number)
    const total = h * 60 + min + durationMinutes
    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`
}

const getTodayStr = () => {
    const t = new Date()
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`
}

// ─── AVATAR COLORS ────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
    ["#7c5cfc", "#f0eeff"], ["#12b886", "#e6faf4"], ["#f59e0b", "#fef3cd"],
    ["#3b82f6", "#dbeafe"], ["#ec4899", "#fce7f3"], ["#8b5cf6", "#ede9fe"],
]
const avatarColor = (name) => {
    if (!name) return AVATAR_COLORS[0]
    return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length]
}

// ─── SVG ICONS ────────────────────────────────────────────────────────────────
const CheckIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </svg>
)
const XIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
)
const NoteIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    </svg>
)
const CalendarIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
)
const ClockIcon = ({ size = 13 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
    </svg>
)
const UserIcon = ({ size = 13 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
)
const ScissorsIcon = ({ size = 13 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="6" cy="6" r="3" /><circle cx="6" cy="18" r="3" />
        <line x1="20" y1="4" x2="8.12" y2="15.88" />
        <line x1="14.47" y1="14.48" x2="20" y2="20" />
        <line x1="8.12" y1="8.12" x2="12" y2="12" />
    </svg>
)
const ListIcon = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" />
        <line x1="8" y1="18" x2="21" y2="18" />
        <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" />
        <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
)
const PhoneIcon = ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
)
const SpinnerIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         style={{ animation: "al-spin 0.8s linear infinite", display: "inline-block" }}>
        <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeOpacity="0.2" />
        <path d="M21 12a9 9 0 00-9-9" />
    </svg>
)
const AllStaffIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
)
const ChevronLeftIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="15 18 9 12 15 6" />
    </svg>
)
const ChevronRightIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="9 18 15 12 9 6" />
    </svg>
)

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────
// KEY FIX: .al-page has NO background — inherits HorizonUI's own layout bg.
// Dark mode variables override color tokens only. No wrapper background ever set.
const GlobalStyles = () => (
    <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

    .al-page {
      font-family: 'Plus Jakarta Sans', sans-serif;

      --al-accent:    #7c5cfc;
      --al-accent-l:  rgba(124,92,252,0.12);
      --al-accent-h:  #6b4edb;
      --al-success:   #12b886;
      --al-success-l: rgba(18,184,134,0.12);
      --al-error:     #fa5252;
      --al-error-l:   rgba(250,82,82,0.12);
      --al-warn:      #f59e0b;
      --al-warn-l:    rgba(245,158,11,0.12);
      --al-info:      #3b82f6;
      --al-info-l:    rgba(59,130,246,0.12);

      /* ── LIGHT (default) ── */
      --al-card:      #ffffff;
      --al-card2:     #f4f2ff;
      --al-border:    rgba(124,92,252,0.12);
      --al-border2:   rgba(0,0,0,0.07);
      --al-text1:     #1a1a2e;
      --al-text2:     #6b7280;
      --al-text3:     #9ca3af;
      --al-shadow:    0 4px 24px rgba(124,92,252,0.07);
      --al-shadow-lg: 0 12px 40px rgba(124,92,252,0.13);
      --al-skel1:     rgba(124,92,252,0.08);
      --al-skel2:     rgba(124,92,252,0.16);
      --al-thead:     #f9f8ff;
    }

    /* ── DARK MODE — only color tokens, NO background set ── */
    [data-coreui-theme="dark"] .al-page,
    [data-bs-theme="dark"] .al-page,
    .dark .al-page {
      --al-card:      #1a1928;
      --al-card2:     #1f1e2e;
      --al-border:    rgba(124,92,252,0.2);
      --al-border2:   rgba(255,255,255,0.06);
      --al-text1:     #f0eeff;
      --al-text2:     #a09bc0;
      --al-text3:     #6b6785;
      --al-shadow:    0 4px 24px rgba(0,0,0,0.35);
      --al-shadow-lg: 0 12px 40px rgba(0,0,0,0.5);
      --al-skel1:     rgba(124,92,252,0.08);
      --al-skel2:     rgba(124,92,252,0.18);
      --al-thead:     #16152a;
    }

    /* ── ANIMATIONS ── */
    @keyframes al-spin    { to { transform: rotate(360deg); } }
    @keyframes al-shimmer { 0% { background-position:200% 0; } 100% { background-position:-200% 0; } }
    @keyframes al-fade-up { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:none; } }
    @keyframes al-modal-in { from { opacity:0; transform:translateY(-14px) scale(0.97); } to { opacity:1; transform:none; } }

    /* ── PAGE: transparent bg, inherits HorizonUI layout ── */
    .al-page { background: transparent; }

    /* ── HEADER CARD ── */
    .al-header-card {
      background: var(--al-card);
      border-radius: 20px; border: 1px solid var(--al-border);
      box-shadow: var(--al-shadow); padding: 20px 24px;
      display: flex; align-items: center; justify-content: space-between;
      flex-wrap: wrap; gap: 12px; margin-bottom: 16px;
    }
    .al-header-left { display: flex; align-items: center; gap: 14px; }
    .al-header-icon {
      width: 48px; height: 48px; border-radius: 14px;
      background: var(--al-accent-l); color: var(--al-accent);
      display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    }
    .al-header-title { font-size: 1.15rem; font-weight: 800; color: var(--al-text1); margin: 0; }
    .al-header-sub   { font-size: 0.78rem; color: var(--al-text3); margin: 2px 0 0; }

    /* ── STAT ROW ── */
    .al-stat-row {
      display: grid; gap: 10px; margin-bottom: 16px;
      grid-template-columns: repeat(2, 1fr);
    }
    @media(min-width:640px) { .al-stat-row { grid-template-columns: repeat(4, 1fr); } }
    .al-stat-card {
      background: var(--al-card); border-radius: 16px;
      border: 1px solid var(--al-border); box-shadow: var(--al-shadow);
      padding: 14px 16px; display: flex; align-items: center; gap: 12px;
    }
    .al-stat-icon {
      width: 40px; height: 40px; border-radius: 12px; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
    }
    .al-stat-label { font-size: 0.67rem; font-weight: 700; color: var(--al-text3); text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 2px; }
    .al-stat-value { font-size: 1.2rem; font-weight: 800; color: var(--al-text1); margin: 0; line-height: 1; }

    /* ── SECTION CARD ── */
    .al-card {
      background: var(--al-card); border-radius: 20px;
      border: 1px solid var(--al-border); box-shadow: var(--al-shadow);
      overflow: hidden; margin-bottom: 16px;
    }
    .al-card-header {
      padding: 16px 20px; border-bottom: 1px solid var(--al-border2);
      display: flex; align-items: flex-start; flex-direction: column; gap: 2px;
    }
    .al-card-header-title { font-size: 0.9rem; font-weight: 800; color: var(--al-text1); margin: 0; }
    .al-card-header-sub   { font-size: 0.75rem; color: var(--al-text3); margin: 0; }

    /* ── STAFF SCROLL ── */
    .al-staff-scroll {
      overflow-x: auto; -webkit-overflow-scrolling: touch;
      scrollbar-width: thin; scrollbar-color: var(--al-accent-l) transparent;
      padding: 16px 20px;
    }
    .al-staff-scroll::-webkit-scrollbar { height: 4px; }
    .al-staff-scroll::-webkit-scrollbar-thumb { background: var(--al-accent-l); border-radius: 4px; }
    .al-staff-row { display: flex; gap: 12px; }

    /* ── STAFF TILE ── */
    .al-staff-tile {
      display: flex; flex-direction: column; align-items: center;
      padding: 16px 14px; background: var(--al-card2);
      border-radius: 18px; border: 2px solid transparent;
      cursor: pointer; transition: all 0.22s ease;
      min-width: 120px; flex-shrink: 0; text-align: center;
    }
    .al-staff-tile:hover { border-color: var(--al-accent); transform: translateY(-3px); box-shadow: 0 8px 24px rgba(124,92,252,0.15); }
    .al-staff-tile.active {
      border-color: var(--al-accent) !important;
      background: var(--al-accent-l) !important;
      transform: translateY(-3px);
      box-shadow: 0 8px 24px rgba(124,92,252,0.2) !important;
    }
    .al-staff-avatar {
      width: 60px; height: 60px; border-radius: 50%; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
      font-size: 1rem; font-weight: 800; margin-bottom: 10px;
      border: 2.5px solid rgba(124,92,252,0.2);
    }
    .al-staff-avatar img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
    .al-staff-name  { font-size: 0.78rem; font-weight: 800; color: var(--al-text1); margin: 0 0 6px; line-height: 1.2; }
    .al-staff-count {
      display: flex; flex-direction: column; gap: 3px;
    }
    .al-staff-count-badge {
      display: inline-flex; align-items: center; gap: 4px;
      font-size: 0.62rem; font-weight: 700; padding: 2px 8px; border-radius: 20px;
    }
    .al-sbc-warn { background: var(--al-warn-l); color: var(--al-warn); }
    .al-sbc-ok   { background: var(--al-success-l); color: var(--al-success); }

    /* ── TABS ── */
    .al-tabs-bar {
      display: flex; gap: 4px; padding: 14px 20px 0;
      border-bottom: 1px solid var(--al-border2);
    }
    .al-tab-btn {
      padding: 9px 16px; border-radius: 10px 10px 0 0;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.8rem; font-weight: 700;
      background: none; border: none; border-bottom: 2.5px solid transparent;
      color: var(--al-text3); cursor: pointer; transition: all 0.18s;
      white-space: nowrap;
    }
    .al-tab-btn:hover { color: var(--al-text1); }
    .al-tab-btn.active { color: var(--al-accent); border-bottom-color: var(--al-accent); }
    .al-sub-tabs-bar {
      display: flex; gap: 4px; padding: 12px 20px 0;
      border-bottom: 1px solid var(--al-border2);
      overflow-x: auto; -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }
    .al-sub-tabs-bar::-webkit-scrollbar { display: none; }
    .al-sub-tab-btn {
      padding: 7px 14px; border-radius: 8px 8px 0 0;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.75rem; font-weight: 700;
      background: none; border: none; border-bottom: 2px solid transparent;
      color: var(--al-text3); cursor: pointer; transition: all 0.18s; white-space: nowrap;
    }
    .al-sub-tab-btn:hover { color: var(--al-text1); }
    .al-sub-tab-btn.active { color: var(--al-accent); border-bottom-color: var(--al-accent); }

    /* ── TABLE ── */
    .al-table-wrap { overflow-x: auto; }
    .al-table { width: 100%; border-collapse: collapse; }
    .al-table thead tr { background: var(--al-thead); border-bottom: 1.5px solid var(--al-border2); }
    .al-table thead th {
      padding: 11px 16px; font-size: 0.68rem; font-weight: 700;
      color: var(--al-text3); text-transform: uppercase; letter-spacing: 0.6px; white-space: nowrap;
    }
    .al-table tbody tr { border-bottom: 1px solid var(--al-border2); transition: background 0.12s; }
    .al-table tbody tr:last-child { border-bottom: none; }
    .al-table tbody tr:hover { background: var(--al-card2); }
    .al-table tbody td { padding: 12px 16px; font-size: 0.85rem; color: var(--al-text1); vertical-align: middle; }

    /* ── BADGES ── */
    .al-badge {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 3px 9px; border-radius: 20px;
      font-size: 0.65rem; font-weight: 700; white-space: nowrap;
    }
    .al-badge-warn    { background: var(--al-warn-l);    color: var(--al-warn); }
    .al-badge-ok      { background: var(--al-success-l); color: var(--al-success); }
    .al-badge-accent  { background: var(--al-accent-l);  color: var(--al-accent); }
    .al-badge-info    { background: var(--al-info-l);    color: var(--al-info); }

    /* ── ICON BUTTONS ── */
    .al-icon-btn {
      width: 32px; height: 32px; border-radius: 8px; padding: 0;
      display: inline-flex; align-items: center; justify-content: center;
      border: 1.5px solid var(--al-border); background: var(--al-card2);
      color: var(--al-text2); cursor: pointer; transition: all 0.15s;
      font-family: 'Plus Jakarta Sans', sans-serif;
    }
    .al-icon-btn:hover:not(:disabled)       { border-color: var(--al-accent);  color: var(--al-accent);  background: var(--al-accent-l); }
    .al-icon-btn.success:hover:not(:disabled){ border-color: var(--al-success); color: var(--al-success); background: var(--al-success-l); }
    .al-icon-btn.danger:hover:not(:disabled) { border-color: var(--al-error);   color: var(--al-error);   background: var(--al-error-l); }
    .al-icon-btn:disabled { opacity: 0.35; cursor: not-allowed; }

    /* ── BUTTONS ── */
    .al-btn {
      display: inline-flex; align-items: center; justify-content: center; gap: 6px;
      padding: 7px 14px; border-radius: 9px;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.78rem; font-weight: 700;
      cursor: pointer; transition: all 0.18s; border: none; white-space: nowrap;
    }
    .al-btn-success { background: var(--al-success); color: #fff; }
    .al-btn-success:hover:not(:disabled) { background: #0ca678; box-shadow: 0 4px 12px rgba(18,184,134,0.3); }
    .al-btn-danger  { background: var(--al-error-l); color: var(--al-error); border: 1.5px solid var(--al-error); }
    .al-btn-danger:hover:not(:disabled)  { background: var(--al-error); color: #fff; }
    .al-btn-ghost   { background: var(--al-card2); color: var(--al-text2); border: 1.5px solid var(--al-border); }
    .al-btn-ghost:hover:not(:disabled)   { border-color: var(--al-accent); color: var(--al-accent); }
    .al-btn-note    { background: var(--al-info-l); color: var(--al-info); border: 1.5px solid rgba(59,130,246,0.3); }
    .al-btn-note:hover:not(:disabled)    { background: var(--al-info); color: #fff; }
    .al-btn:disabled { opacity: 0.4; cursor: not-allowed; }

    /* ── MOBILE CARDS ── */
    .al-mobile-list { display: flex; flex-direction: column; gap: 10px; padding: 14px 16px; }
    .al-appt-card {
      background: var(--al-card2); border-radius: 16px;
      border: 1.5px solid var(--al-border); overflow: hidden;
      position: relative; animation: al-fade-up 0.25s ease both;
    }
    .al-appt-card-strip { position: absolute; top: 0; left: 0; bottom: 0; width: 4px; border-radius: 4px 0 0 4px; }
    .al-appt-card-body  { padding: 13px 13px 11px 18px; }
    .al-appt-card-top   { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; gap: 8px; }
    .al-appt-customer   { font-size: 0.88rem; font-weight: 800; color: var(--al-text1); margin: 0 0 2px; display: flex; align-items: center; flex-wrap: wrap; gap: 5px; }
    .al-appt-phone      { font-size: 0.72rem; color: var(--al-text3); display: flex; align-items: center; gap: 3px; }
    .al-appt-meta       { display: flex; flex-direction: column; gap: 5px; margin-bottom: 10px; }
    .al-appt-meta-row   { display: flex; align-items: center; gap: 6px; font-size: 0.78rem; color: var(--al-text2); }
    .al-appt-meta-label { color: var(--al-text3); font-weight: 600; font-size: 0.7rem; min-width: 52px; }
    .al-appt-meta-val   { font-weight: 700; color: var(--al-text1); }
    .al-appt-actions    { display: flex; gap: 7px; flex-wrap: wrap; }
    .al-appt-actions .al-btn { flex: 1; min-width: 0; font-size: 0.73rem; padding: 7px 8px; }

    /* ── SKELETON ── */
    .al-skeleton {
      background: linear-gradient(90deg, var(--al-skel1) 25%, var(--al-skel2) 50%, var(--al-skel1) 75%);
      background-size: 200% 100%; animation: al-shimmer 1.5s infinite; border-radius: 6px;
    }

    /* ── PAGINATION ── */
    .al-pagination {
      display: flex; align-items: center; justify-content: center; gap: 4px;
      padding: 14px 20px; border-top: 1px solid var(--al-border2);
    }
    .al-page-btn {
      min-width: 32px; height: 32px; padding: 0 8px; border-radius: 8px;
      display: inline-flex; align-items: center; justify-content: center;
      border: 1.5px solid var(--al-border); background: var(--al-card2);
      color: var(--al-text2); font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.75rem; font-weight: 700; cursor: pointer; transition: all 0.15s;
    }
    .al-page-btn:hover:not(:disabled):not(.active) { border-color: var(--al-accent); color: var(--al-accent); background: var(--al-accent-l); }
    .al-page-btn.active { background: var(--al-accent); border-color: var(--al-accent); color: #fff; }
    .al-page-btn:disabled { opacity: 0.35; cursor: not-allowed; }

    /* ── ALERT TOAST ── */
    .al-alert-toast {
      display: flex; align-items: center; gap: 10px;
      padding: 12px 16px; border-radius: 14px;
      font-size: 0.85rem; font-weight: 600;
      margin-bottom: 14px; animation: al-fade-up 0.25s ease;
      box-shadow: var(--al-shadow);
    }
    .al-alert-success { background: var(--al-success-l); color: var(--al-success); border: 1px solid rgba(18,184,134,0.25); }
    .al-alert-warning { background: var(--al-warn-l);    color: var(--al-warn);    border: 1px solid rgba(245,158,11,0.25); }
    .al-alert-danger  { background: var(--al-error-l);   color: var(--al-error);   border: 1px solid rgba(250,82,82,0.25); }
    .al-alert-close { background: none; border: none; cursor: pointer; color: inherit; opacity: 0.6; padding: 0; margin-left: auto; flex-shrink: 0; }
    .al-alert-close:hover { opacity: 1; }

    /* ── MODAL ── */
    .al-modal-overlay {
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
      z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 16px;
    }
    .al-modal {
      background: var(--al-card); border-radius: 20px;
      border: 1px solid var(--al-border); box-shadow: var(--al-shadow-lg);
      width: 100%; max-width: 440px;
      animation: al-modal-in 0.2s ease both;
      display: flex; flex-direction: column;
    }
    .al-modal-header {
      padding: 16px 20px; border-bottom: 1px solid var(--al-border2);
      display: flex; align-items: center; justify-content: space-between;
    }
    .al-modal-title { font-size: 0.95rem; font-weight: 800; color: var(--al-text1); }
    .al-modal-close {
      width: 30px; height: 30px; border-radius: 8px;
      background: var(--al-card2); border: none; cursor: pointer;
      color: var(--al-text3); display: flex; align-items: center; justify-content: center;
      transition: all 0.15s;
    }
    .al-modal-close:hover { background: var(--al-error-l); color: var(--al-error); }
    .al-modal-body   { padding: 20px; }
    .al-modal-footer { padding: 12px 20px; border-top: 1px solid var(--al-border2); display: flex; justify-content: flex-end; }

    /* ── NOTE QUOTE ── */
    .al-note-quote {
      background: var(--al-card2); border-left: 3px solid var(--al-accent);
      border-radius: 0 12px 12px 0;
      padding: 12px 16px; font-size: 0.88rem;
      color: var(--al-text1); line-height: 1.6;
      font-style: italic;
    }

    /* ── EMPTY STATE ── */
    .al-empty {
      display: flex; flex-direction: column; align-items: center;
      padding: 48px 24px; text-align: center;
    }
    .al-empty-icon {
      width: 64px; height: 64px; border-radius: 20px;
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 14px;
    }
    .al-empty-title { font-size: 0.95rem; font-weight: 800; color: var(--al-text1); margin: 0 0 6px; }
    .al-empty-sub   { font-size: 0.8rem; color: var(--al-text3); margin: 0; }

    /* ── FULL PAGE LOADER ── */
    .al-full-loader {
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      min-height: 300px; gap: 14px;
    }
    .al-full-spinner {
      width: 44px; height: 44px; border-radius: 50%;
      border: 3px solid var(--al-accent-l); border-top-color: var(--al-accent);
      animation: al-spin 0.8s linear infinite;
    }

    /* ── CUSTOMER TAG ── */
    .al-customer-tag {
      font-size: 0.6rem; font-weight: 800; padding: 2px 7px; border-radius: 20px;
      background: var(--al-info-l); color: var(--al-info);
    }

    /* ── TIME RANGE ── */
    .al-time-range { font-size: 0.75rem; color: var(--al-text3); display: flex; align-items: center; gap: 4px; margin-top: 2px; }

    /* ── FUTURE DISABLED HINT ── */
    .al-future-hint { font-size: 0.68rem; color: var(--al-text3); font-style: italic; margin-top: 3px; }
  `}</style>
)

// ─── PAGINATION ───────────────────────────────────────────────────────────────
const PAGE_SIZE = 12

const Pagination = ({ page, total, onPage }) => {
    if (total <= 1) return null
    return (
        <div className="al-pagination">
            <button className="al-page-btn" onClick={() => onPage(1)} disabled={page === 1}>«</button>
            <button className="al-page-btn" onClick={() => onPage(page - 1)} disabled={page === 1}><ChevronLeftIcon /></button>
            <span style={{ padding: "0 10px", fontSize: "0.78rem", fontWeight: 700, color: "var(--al-text2)", whiteSpace: "nowrap" }}>
        {page} / {total}
      </span>
            <button className="al-page-btn" onClick={() => onPage(page + 1)} disabled={page === total}><ChevronRightIcon /></button>
            <button className="al-page-btn" onClick={() => onPage(total)} disabled={page === total}>»</button>
        </div>
    )
}

// ─── MODAL ────────────────────────────────────────────────────────────────────
const NoteModal = ({ visible, note, onClose }) => {
    if (!visible) return null
    return (
        <div className="al-modal-overlay" onClick={(e) => e.target.classList.contains("al-modal-overlay") && onClose()}>
            <div className="al-modal">
                <div className="al-modal-header">
          <span className="al-modal-title">
            <NoteIcon size={15} style={{ marginRight: 6 }} /> Müşteri Randevu Notu
          </span>
                    <button className="al-modal-close" onClick={onClose}><XIcon size={14} /></button>
                </div>
                <div className="al-modal-body">
                    <div className="al-note-quote">{note || "Not bulunamadı."}</div>
                </div>
                <div className="al-modal-footer">
                    <button className="al-btn al-btn-ghost" onClick={onClose}>Kapat</button>
                </div>
            </div>
        </div>
    )
}

// ─── APPOINTMENT ROW (desktop table) ─────────────────────────────────────────
const PendingTableRow = ({ app, onStatusChange, onNote, loadingId }) => {
    const customerName = app.customer ? `${app.customer.name} ${app.customer.surname}` : "Bilinmeyen"
    const staffName    = app.staff    ? `${app.staff.name} ${app.staff.surname}`       : "Bilinmeyen"
    const isLoading    = loadingId === app.id

    return (
        <tr>
            <td>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700 }}>{customerName}</span>
                    {app.customer?.tag && <span className="al-customer-tag">{app.customer.tag}</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.72rem", color: "var(--al-text3)", marginTop: 2 }}>
                    <PhoneIcon /> {app.customer?.phone || "-"}
                </div>
            </td>
            <td style={{ fontWeight: 600 }}>{staffName}</td>
            <td>
                <span style={{ fontWeight: 600 }}>{app.service?.name || "Bilinmeyen"}</span>
                {app.service?.duration && (
                    <div className="al-time-range"><ClockIcon /> {app.service.duration} dk</div>
                )}
            </td>
            <td>
                <div style={{ fontWeight: 700 }}>{formatDateWithDay(app.appointment_date)}</div>
                <div className="al-time-range">
                    <ClockIcon /> {app.appointment_time} → {calculateEndTime(app.appointment_time, app.service?.duration)}
                </div>
            </td>
            <td style={{ textAlign: "center" }}>
                <button
                    className="al-icon-btn"
                    title={app.note ? "Notu Görüntüle" : "Not Yok"}
                    disabled={!app.note}
                    onClick={() => onNote(app.note)}
                    style={app.note ? { color: "var(--al-info)", borderColor: "rgba(59,130,246,0.3)" } : {}}
                >
                    <NoteIcon />
                </button>
            </td>
            <td style={{ textAlign: "center" }}>
                <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
                    <button
                        className="al-icon-btn success" title="Onayla"
                        disabled={isLoading}
                        onClick={() => onStatusChange(app.id, "approved")}
                    >
                        {isLoading ? <SpinnerIcon size={13} /> : <CheckIcon />}
                    </button>
                    <button
                        className="al-icon-btn danger" title="Reddet"
                        disabled={isLoading}
                        onClick={() => onStatusChange(app.id, "cancelled")}
                    >
                        <XIcon />
                    </button>
                </div>
            </td>
        </tr>
    )
}

const ApprovedTableRow = ({ app, approvedSubTab, onStatusChange, onNote, loadingId }) => {
    const now           = new Date()
    const appDateStr    = getLocalDateString(app.appointment_date)
    const [y, m, d]     = appDateStr.split("-").map(Number)
    const [hr, min]     = app.appointment_time.split(":").map(Number)
    const appDateTime   = new Date(y, m - 1, d, hr, min)
    const isFuture      = appDateTime > now
    const customerName  = app.customer ? `${app.customer.name} ${app.customer.surname}` : "Bilinmeyen"
    const staffName     = app.staff    ? `${app.staff.name} ${app.staff.surname}`       : "Bilinmeyen"
    const isLoading     = loadingId === app.id

    return (
        <tr>
            <td>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700 }}>{customerName}</span>
                    {app.customer?.tag && <span className="al-customer-tag">{app.customer.tag}</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.72rem", color: "var(--al-text3)", marginTop: 2 }}>
                    <PhoneIcon /> {app.customer?.phone || "-"}
                </div>
            </td>
            <td style={{ fontWeight: 600 }}>{staffName}</td>
            <td>
                <span style={{ fontWeight: 600 }}>{app.service?.name || "Bilinmeyen"}</span>
                {app.service?.duration && (
                    <div className="al-time-range"><ClockIcon /> {app.service.duration} dk</div>
                )}
            </td>
            <td>
                {approvedSubTab !== "today" && (
                    <div style={{ fontWeight: 700 }}>{formatDateWithDay(app.appointment_date)}</div>
                )}
                <div className={approvedSubTab !== "today" ? "al-time-range" : ""} style={{ fontWeight: approvedSubTab === "today" ? 700 : 400 }}>
                    <ClockIcon /> {app.appointment_time}
                </div>
            </td>
            <td style={{ textAlign: "center" }}>
                <button
                    className="al-icon-btn"
                    title={app.note ? "Notu Görüntüle" : "Not Yok"}
                    disabled={!app.note}
                    onClick={() => onNote(app.note)}
                    style={app.note ? { color: "var(--al-info)", borderColor: "rgba(59,130,246,0.3)" } : {}}
                >
                    <NoteIcon />
                </button>
            </td>
            <td style={{ textAlign: "center" }}>
                <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
                    <button
                        className="al-icon-btn success"
                        title={isFuture ? "Randevu saati henüz gelmedi" : "Tamamlandı olarak işaretle"}
                        disabled={isFuture || isLoading}
                        onClick={() => onStatusChange(app.id, "completed")}
                    >
                        {isLoading ? <SpinnerIcon size={13} /> : <CheckIcon />}
                    </button>
                    <button
                        className="al-icon-btn danger" title="İptal Et"
                        disabled={isLoading}
                        onClick={() => onStatusChange(app.id, "cancelled")}
                    >
                        <XIcon />
                    </button>
                </div>
                {isFuture && <div className="al-future-hint">Saat gelmedi</div>}
            </td>
        </tr>
    )
}

// ─── APPOINTMENT TABLE (desktop + mobile) ────────────────────────────────────
const PendingContent = ({ appointments, onStatusChange, onNote, page, setPage, loadingId }) => {
    const totalPages = Math.max(1, Math.ceil(appointments.length / PAGE_SIZE))
    const paginated  = appointments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

    if (appointments.length === 0) {
        return (
            <div className="al-empty">
                <div className="al-empty-icon" style={{ background: "var(--al-success-l)", color: "var(--al-success)" }}>
                    <CheckIcon size={28} />
                </div>
                <h3 className="al-empty-title">Bekleyen randevu talebi yok.</h3>
                <p className="al-empty-sub">Tüm talepler işlenmiş durumda.</p>
            </div>
        )
    }

    return (
        <>
            {/* Desktop */}
            <div className="d-none d-md-block al-table-wrap">
                <table className="al-table">
                    <thead>
                    <tr>
                        <th>Müşteri</th><th>Personel</th><th>Hizmet</th>
                        <th>Tarih &amp; Saat</th><th style={{ textAlign: "center" }}>Not</th>
                        <th style={{ textAlign: "center" }}>İşlemler</th>
                    </tr>
                    </thead>
                    <tbody>
                    {paginated.map((app) => (
                        <PendingTableRow key={app.id} app={app} onStatusChange={onStatusChange} onNote={onNote} loadingId={loadingId} />
                    ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile */}
            <div className="d-md-none al-mobile-list">
                {paginated.map((app, idx) => {
                    const isLoading = loadingId === app.id
                    return (
                        <div className="al-appt-card" key={app.id} style={{ animationDelay: `${idx * 0.04}s` }}>
                            <div className="al-appt-card-strip" style={{ background: "var(--al-warn)" }} />
                            <div className="al-appt-card-body">
                                <div className="al-appt-card-top">
                                    <div>
                                        <p className="al-appt-customer">
                                            {app.customer ? `${app.customer.name} ${app.customer.surname}` : "Bilinmeyen"}
                                            {app.customer?.tag && <span className="al-customer-tag">{app.customer.tag}</span>}
                                        </p>
                                        <p className="al-appt-phone"><PhoneIcon /> {app.customer?.phone || "-"}</p>
                                    </div>
                                    <span className="al-badge al-badge-warn">Beklemede</span>
                                </div>
                                <div className="al-appt-meta">
                                    <div className="al-appt-meta-row">
                                        <span className="al-appt-meta-label"><UserIcon /> Personel</span>
                                        <span className="al-appt-meta-val">{app.staff ? `${app.staff.name} ${app.staff.surname}` : "-"}</span>
                                    </div>
                                    <div className="al-appt-meta-row">
                                        <span className="al-appt-meta-label"><ScissorsIcon /> Hizmet</span>
                                        <span className="al-appt-meta-val">{app.service?.name || "-"}</span>
                                    </div>
                                    <div className="al-appt-meta-row">
                                        <span className="al-appt-meta-label"><CalendarIcon size={12} /> Tarih</span>
                                        <span className="al-appt-meta-val">{formatDateWithDay(app.appointment_date)}</span>
                                    </div>
                                    <div className="al-appt-meta-row">
                                        <span className="al-appt-meta-label"><ClockIcon /> Saat</span>
                                        <span className="al-appt-meta-val">
                      {app.appointment_time} → {calculateEndTime(app.appointment_time, app.service?.duration)}
                    </span>
                                    </div>
                                </div>
                                <div className="al-appt-actions">
                                    <button className="al-btn al-btn-note" disabled={!app.note} onClick={() => onNote(app.note)}>
                                        <NoteIcon size={12} /> {app.note ? "Notu Gör" : "Not Yok"}
                                    </button>
                                    <button className="al-btn al-btn-success" disabled={isLoading} onClick={() => onStatusChange(app.id, "approved")}>
                                        {isLoading ? <SpinnerIcon size={12} /> : <CheckIcon size={12} />} Onayla
                                    </button>
                                    <button className="al-btn al-btn-danger" disabled={isLoading} onClick={() => onStatusChange(app.id, "cancelled")}>
                                        <XIcon size={12} /> Reddet
                                    </button>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>

            <Pagination page={page} total={totalPages} onPage={setPage} />
        </>
    )
}

const ApprovedContent = ({ appointments, approvedSubTab, onStatusChange, onNote, page, setPage, loadingId }) => {
    const now         = new Date()
    const totalPages  = Math.max(1, Math.ceil(appointments.length / PAGE_SIZE))
    const paginated   = appointments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

    if (appointments.length === 0) {
        return (
            <div className="al-empty">
                <div className="al-empty-icon" style={{ background: "var(--al-info-l)", color: "var(--al-info)" }}>
                    <CalendarIcon size={26} />
                </div>
                <h3 className="al-empty-title">Bu tarih için randevu yok.</h3>
                <p className="al-empty-sub">Seçili tarihte onaylanmış randevu bulunmamaktadır.</p>
            </div>
        )
    }

    return (
        <>
            {/* Desktop */}
            <div className="d-none d-md-block al-table-wrap">
                <table className="al-table">
                    <thead>
                    <tr>
                        <th>Müşteri</th><th>Personel</th><th>Hizmet</th>
                        <th>Tarih &amp; Saat</th><th style={{ textAlign: "center" }}>Not</th>
                        <th style={{ textAlign: "center" }}>İşlemler</th>
                    </tr>
                    </thead>
                    <tbody>
                    {paginated.map((app) => (
                        <ApprovedTableRow key={app.id} app={app} approvedSubTab={approvedSubTab}
                                          onStatusChange={onStatusChange} onNote={onNote} loadingId={loadingId} />
                    ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile */}
            <div className="d-md-none al-mobile-list">
                {paginated.map((app, idx) => {
                    const appDateStr  = getLocalDateString(app.appointment_date)
                    const [y, m, d]   = appDateStr.split("-").map(Number)
                    const [hr, min]   = app.appointment_time.split(":").map(Number)
                    const isFuture    = new Date(y, m - 1, d, hr, min) > now
                    const isLoading   = loadingId === app.id

                    return (
                        <div className="al-appt-card" key={app.id} style={{ animationDelay: `${idx * 0.04}s` }}>
                            <div className="al-appt-card-strip" style={{ background: "var(--al-accent)" }} />
                            <div className="al-appt-card-body">
                                <div className="al-appt-card-top">
                                    <div>
                                        <p className="al-appt-customer">
                                            {app.customer ? `${app.customer.name} ${app.customer.surname}` : "Bilinmeyen"}
                                            {app.customer?.tag && <span className="al-customer-tag">{app.customer.tag}</span>}
                                        </p>
                                        <p className="al-appt-phone"><PhoneIcon /> {app.customer?.phone || "-"}</p>
                                    </div>
                                    <span className="al-badge al-badge-accent">Onaylandı</span>
                                </div>
                                <div className="al-appt-meta">
                                    <div className="al-appt-meta-row">
                                        <span className="al-appt-meta-label"><UserIcon /> Personel</span>
                                        <span className="al-appt-meta-val">{app.staff ? `${app.staff.name} ${app.staff.surname}` : "-"}</span>
                                    </div>
                                    <div className="al-appt-meta-row">
                                        <span className="al-appt-meta-label"><ScissorsIcon /> Hizmet</span>
                                        <span className="al-appt-meta-val">{app.service?.name || "-"}</span>
                                    </div>
                                    {approvedSubTab !== "today" && (
                                        <div className="al-appt-meta-row">
                                            <span className="al-appt-meta-label"><CalendarIcon size={12} /> Tarih</span>
                                            <span className="al-appt-meta-val">{formatDateWithDay(app.appointment_date)}</span>
                                        </div>
                                    )}
                                    <div className="al-appt-meta-row">
                                        <span className="al-appt-meta-label"><ClockIcon /> Saat</span>
                                        <span className="al-appt-meta-val">{app.appointment_time}</span>
                                    </div>
                                </div>
                                <div className="al-appt-actions">
                                    <button className="al-btn al-btn-note" disabled={!app.note} onClick={() => onNote(app.note)}>
                                        <NoteIcon size={12} /> {app.note ? "Not" : "Not Yok"}
                                    </button>
                                    <button className="al-btn al-btn-success" disabled={isFuture || isLoading}
                                            onClick={() => onStatusChange(app.id, "completed")}
                                            title={isFuture ? "Randevu saati henüz gelmedi" : "Tamamla"}>
                                        {isLoading ? <SpinnerIcon size={12} /> : <CheckIcon size={12} />} Tamamla
                                    </button>
                                    <button className="al-btn al-btn-danger" disabled={isLoading}
                                            onClick={() => onStatusChange(app.id, "cancelled")}>
                                        <XIcon size={12} /> İptal
                                    </button>
                                </div>
                                {isFuture && <div className="al-future-hint" style={{ marginTop: 6 }}>Randevu saati henüz gelmedi, tamamlanamaz.</div>}
                            </div>
                        </div>
                    )
                })}
            </div>

            <Pagination page={page} total={totalPages} onPage={setPage} />
        </>
    )
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const AdminAppointmentList = () => {
    const [staffList,           setStaffList]           = useState([])
    const [allAppointments,     setAllAppointments]     = useState([])
    const [selectedStaff,       setSelectedStaff]       = useState("all")
    const [filteredAppointments,setFilteredAppointments]= useState([])
    const [loading,             setLoading]             = useState(true)
    const [error,               setError]               = useState(null)
    const [activeTab,           setActiveTab]           = useState("pending")
    const [approvedSubTab,      setApprovedSubTab]      = useState("today")
    const [modalVisible,        setModalVisible]        = useState(false)
    const [selectedNote,        setSelectedNote]        = useState("")
    const [alertMessage,        setAlertMessage]        = useState("")
    const [alertColor,          setAlertColor]          = useState("success")
    const [showAlert,           setShowAlert]           = useState(false)
    const [loadingId,           setLoadingId]           = useState(null) // per-row loader

    // Pagination state per tab
    const [pendingPage,  setPendingPage]  = useState(1)
    const [todayPage,    setTodayPage]    = useState(1)
    const [tomorrowPage, setTomorrowPage] = useState(1)
    const [futurePage,   setFuturePage]   = useState(1)

    const { logout } = useAuth()
    const todayStr = getTodayStr()

    // ── Fetch
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                setLoading(true)
                setError(null)
                const [staffRes, apptRes] = await Promise.all([usersAPI.getStaff(), appointmentsAPI.getAll()])
                if (!staffRes?.success || !apptRes?.success) throw new Error("Veriler yüklenirken bir hata oluştu.")
                setStaffList(staffRes.staff || [])
                setAllAppointments(apptRes.appointments || [])
            } catch (err) {
                if (String(err.message).includes("401") || String(err.message).includes("Unauthorized")) {
                    logout()
                    return
                }
                setError(err.message || "Veriler yüklenirken bir hata oluştu.")
            } finally {
                setLoading(false)
            }
        }
        fetchInitialData()
    }, [])

    useEffect(() => {
        setFilteredAppointments(
            selectedStaff === "all"
                ? allAppointments
                : allAppointments.filter((a) => a.staff_id === selectedStaff)
        )
        // Reset pages on filter change
        setPendingPage(1); setTodayPage(1); setTomorrowPage(1); setFuturePage(1)
    }, [selectedStaff, allAppointments])

    // ── Status change handler with per-row loader
    const handleStatusChange = async (appointmentId, newStatus) => {
        setLoadingId(appointmentId)
        try {
            const response = await appointmentsAPI.updateStatus(appointmentId, newStatus)
            if (response.success) {
                setAllAppointments((prev) =>
                    prev.map((a) => (a.id === appointmentId ? { ...a, status: newStatus } : a))
                )
                const msgs = {
                    approved:  { msg: "Randevu başarıyla onaylandı.",              color: "success" },
                    cancelled: { msg: "Randevu iptal edildi.",                     color: "warning" },
                    completed: { msg: "Randevu tamamlandı olarak işaretlendi.",    color: "success" },
                }
                const { msg, color } = msgs[newStatus] || { msg: "İşlem başarılı.", color: "success" }
                setAlertMessage(msg); setAlertColor(color)
                setShowAlert(true)
                setTimeout(() => setShowAlert(false), 3000)
            }
        } catch {
            setAlertMessage("İşlem sırasında bir hata oluştu.")
            setAlertColor("danger")
            setShowAlert(true)
        } finally {
            setLoadingId(null)
        }
    }

    const showNoteModal = (note) => {
        setSelectedNote(note || "Müşteri bir not eklememiş.")
        setModalVisible(true)
    }

    // ── Categorize
    const categorizePendingAppointments = () =>
        filteredAppointments
            .filter((a) => a.status === "pending")
            .sort((a, b) => {
                const da = getLocalDateString(a.appointment_date)
                const db = getLocalDateString(b.appointment_date)
                return da === db ? a.appointment_time.localeCompare(b.appointment_time) : da.localeCompare(db)
            })

    const categorizeApprovedAppointments = () => {
        const tom = new Date()
        tom.setDate(tom.getDate() + 1)
        const tomorrowStr = `${tom.getFullYear()}-${String(tom.getMonth() + 1).padStart(2, "0")}-${String(tom.getDate()).padStart(2, "0")}`
        const approved = filteredAppointments.filter((a) => a.status === "approved")
        return {
            todayAppts:    approved.filter((a) => getLocalDateString(a.appointment_date) === todayStr),
            tomorrowAppts: approved.filter((a) => getLocalDateString(a.appointment_date) === tomorrowStr),
            futureAppts:   approved.filter((a) => getLocalDateString(a.appointment_date) > tomorrowStr),
        }
    }

    const calculateAppointmentCounts = (staffId = null) => {
        const appts = staffId === null ? allAppointments : allAppointments.filter((a) => a.staff_id === staffId)
        return {
            pendingCount:  appts.filter((a) => a.status === "pending").length,
            approvedCount: appts.filter((a) => a.status === "approved" && getLocalDateString(a.appointment_date) >= todayStr).length,
        }
    }

    // ── Loading / Error states
    if (loading) {
        return (
            <div className="al-page">
                <GlobalStyles />
                <div className="al-full-loader">
                    <div className="al-full-spinner" />
                    <p style={{ fontSize: "0.85rem", color: "var(--al-text3)", fontWeight: 600, margin: 0 }}>
                        Randevular yükleniyor...
                    </p>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="al-page">
                <GlobalStyles />
                <div className="al-alert-toast al-alert-danger" style={{ maxWidth: 500 }}>
                    <XIcon size={16} /> <strong>Hata!</strong> {error}
                </div>
            </div>
        )
    }

    const pendingAppointments = categorizePendingAppointments()
    const { todayAppts, tomorrowAppts, futureAppts } = categorizeApprovedAppointments()
    const allCounts = calculateAppointmentCounts(null)

    // ── Staff filter tile label
    const activeStaffLabel = selectedStaff === "all"
        ? "Tüm Randevular"
        : (() => { const s = staffList.find((x) => x.id === selectedStaff); return s ? `${s.name} ${s.surname}` : "" })()

    // ─── RENDER ────────────────────────────────────────────────────────────────
    return (
        <div className="al-page">
            <GlobalStyles />

            {/* ── HEADER ── */}
            <div className="al-header-card">
                <div className="al-header-left">
                    <div className="al-header-icon"><ListIcon /></div>
                    <div>
                        <h1 className="al-header-title">Randevu Yönetimi</h1>
                        <p className="al-header-sub">Tüm randevuları görüntüleyin ve yönetin.</p>
                    </div>
                </div>
            </div>

            {/* ── STAT ROW ── */}
            <div className="al-stat-row">
                {[
                    { label: "Onay Bekleyen",     value: allCounts.pendingCount,  bg: "var(--al-warn-l)",    color: "var(--al-warn)",    icon: <ClockIcon size={16} /> },
                    { label: "Onaylanmış",        value: allCounts.approvedCount, bg: "var(--al-success-l)", color: "var(--al-success)", icon: <CheckIcon size={16} /> },
                    { label: "Bugün",             value: todayAppts.length,       bg: "var(--al-accent-l)",  color: "var(--al-accent)",  icon: <CalendarIcon size={16} /> },
                    { label: "Yarın",             value: tomorrowAppts.length,    bg: "var(--al-info-l)",    color: "var(--al-info)",    icon: <CalendarIcon size={16} /> },
                ].map((s, i) => (
                    <div className="al-stat-card" key={i}>
                        <div className="al-stat-icon" style={{ background: s.bg, color: s.color }}>{s.icon}</div>
                        <div>
                            <p className="al-stat-label">{s.label}</p>
                            <p className="al-stat-value">{s.value}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── ALERT ── */}
            {showAlert && (
                <div className={`al-alert-toast al-alert-${alertColor}`}>
                    <CheckIcon size={15} />
                    <span style={{ flex: 1 }}>{alertMessage}</span>
                    <button className="al-alert-close" onClick={() => setShowAlert(false)}><XIcon size={14} /></button>
                </div>
            )}

            {/* ── STAFF SECTION ── */}
            <div className="al-card">
                <div className="al-card-header">
                    <p className="al-card-header-title">1. Personel Seçin</p>
                    <p className="al-card-header-sub">Randevularını görmek istediğiniz personeli seçin veya tüm randevuları görüntüleyin.</p>
                </div>
                <div className="al-staff-scroll">
                    <div className="al-staff-row">
                        {/* ALL tile */}
                        <div
                            className={`al-staff-tile ${selectedStaff === "all" ? "active" : ""}`}
                            onClick={() => setSelectedStaff("all")}
                            role="button" tabIndex={0}
                            onKeyDown={(e) => e.key === "Enter" && setSelectedStaff("all")}
                        >
                            <div className="al-staff-avatar" style={{ background: "var(--al-accent-l)", color: "var(--al-accent)" }}>
                                <AllStaffIcon />
                            </div>
                            <p className="al-staff-name">Tüm Randevular</p>
                            <div className="al-staff-count">
                <span className="al-staff-count-badge al-sbc-warn">
                  <ClockIcon size={10} /> {allCounts.pendingCount} Bekleyen
                </span>
                                <span className="al-staff-count-badge al-sbc-ok">
                  <CheckIcon size={10} /> {allCounts.approvedCount} Onaylı
                </span>
                            </div>
                        </div>

                        {/* Staff tiles */}
                        {staffList.map((staff) => {
                            const fullName    = `${staff.name} ${staff.surname}`.trim()
                            const isActive    = selectedStaff === staff.id
                            const counts      = calculateAppointmentCounts(staff.id)
                            const [bg, fg]    = avatarColor(staff.name)

                            return (
                                <div
                                    key={staff.id}
                                    className={`al-staff-tile ${isActive ? "active" : ""}`}
                                    onClick={() => setSelectedStaff(staff.id)}
                                    role="button" tabIndex={0}
                                    onKeyDown={(e) => e.key === "Enter" && setSelectedStaff(staff.id)}
                                >
                                    <div className="al-staff-avatar" style={{ background: bg, color: fg }}>
                                        {staff.profile_picture
                                            ? <img src={staff.profile_picture} alt={fullName} />
                                            : getInitials(fullName)
                                        }
                                    </div>
                                    <p className="al-staff-name">{fullName}</p>
                                    <div className="al-staff-count">
                    <span className="al-staff-count-badge al-sbc-warn">
                      <ClockIcon size={10} /> {counts.pendingCount} Bekleyen
                    </span>
                                        <span className="al-staff-count-badge al-sbc-ok">
                      <CheckIcon size={10} /> {counts.approvedCount} Onaylı
                    </span>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>

            {/* ── APPOINTMENTS CARD ── */}
            <div className="al-card">
                <div className="al-card-header">
                    <p className="al-card-header-title">
                        2. Randevular: {activeStaffLabel}
                    </p>
                </div>

                {/* Main Tabs */}
                <div className="al-tabs-bar">
                    <button
                        className={`al-tab-btn ${activeTab === "pending" ? "active" : ""}`}
                        onClick={() => { setActiveTab("pending"); setPendingPage(1) }}
                    >
                        Onay Bekleyen ({pendingAppointments.length})
                    </button>
                    <button
                        className={`al-tab-btn ${activeTab === "approved" ? "active" : ""}`}
                        onClick={() => { setActiveTab("approved") }}
                    >
                        Onaylanmış ({todayAppts.length + tomorrowAppts.length + futureAppts.length})
                    </button>
                </div>

                {/* Pending Tab */}
                {activeTab === "pending" && (
                    <PendingContent
                        appointments={pendingAppointments}
                        onStatusChange={handleStatusChange}
                        onNote={showNoteModal}
                        page={pendingPage}
                        setPage={setPendingPage}
                        loadingId={loadingId}
                    />
                )}

                {/* Approved Tab */}
                {activeTab === "approved" && (
                    <>
                        <div className="al-sub-tabs-bar">
                            {[
                                { key: "today",    label: `Bugün (${todayAppts.length})`,      setter: setTodayPage },
                                { key: "tomorrow", label: `Yarın (${tomorrowAppts.length})`,   setter: setTomorrowPage },
                                { key: "future",   label: `Sonraki Tarihler (${futureAppts.length})`, setter: setFuturePage },
                            ].map(({ key, label }) => (
                                <button
                                    key={key}
                                    className={`al-sub-tab-btn ${approvedSubTab === key ? "active" : ""}`}
                                    onClick={() => setApprovedSubTab(key)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        {approvedSubTab === "today" && (
                            <ApprovedContent
                                appointments={todayAppts} approvedSubTab="today"
                                onStatusChange={handleStatusChange} onNote={showNoteModal}
                                page={todayPage} setPage={setTodayPage} loadingId={loadingId}
                            />
                        )}
                        {approvedSubTab === "tomorrow" && (
                            <ApprovedContent
                                appointments={tomorrowAppts} approvedSubTab="tomorrow"
                                onStatusChange={handleStatusChange} onNote={showNoteModal}
                                page={tomorrowPage} setPage={setTomorrowPage} loadingId={loadingId}
                            />
                        )}
                        {approvedSubTab === "future" && (
                            <ApprovedContent
                                appointments={futureAppts} approvedSubTab="future"
                                onStatusChange={handleStatusChange} onNote={showNoteModal}
                                page={futurePage} setPage={setFuturePage} loadingId={loadingId}
                            />
                        )}
                    </>
                )}
            </div>

            {/* ── NOTE MODAL ── */}
            <NoteModal visible={modalVisible} note={selectedNote} onClose={() => setModalVisible(false)} />
        </div>
    )
}

export default AdminAppointmentList